import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, AbstractControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { Observable, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { County } from 'src/app/model/county';
import { SchoolDistrict } from 'src/app/model/schoolDistrict.model';
import { Region } from 'src/app/model/region.model';
import { HelperService } from 'src/app/service/helper.service';
import { DistrictEntityService } from '../../service/district-entity.service';

@Component({
    selector: 'district-dropdown',
    templateUrl: './district-dropdown.component.html',
    styleUrls: ['./district-dropdown.component.scss']
})
export class DistrictDropdownComponent implements OnInit {
    @Input() selectSchoolDistrictIds: number[] = [];
    @Input() isMultiple: boolean = true;
    @Input() disableAutoComplete: boolean = false;
    @Input() autoRefreshOnRegionChange: boolean = false;
    @Input('reset') reset: boolean;
    @Input() required: boolean = false;
    @Input('disabled') disabled: boolean;
    @Input('customLabel') customLabel: string;
    @Output() eventEmitterSchoolDistrict = new EventEmitter<SchoolDistrict[]>();

    @ViewChild('filterRef') filterRef: ElementRef;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSelect) matSelect: MatSelect;

    schoolDistrictForm: UntypedFormGroup;
    schoolDistrictIds: number[] = [];
    selectedSchoolDistrictIds: number[] = [];

    selectedSchoolDistrictName: string | undefined;
    filteredSchoolDistricts: SchoolDistrict[] = [];
    filteredSchoolDistrictsShow: SchoolDistrict[] = [];

    fieldLabel: string;
    regions: Region[] = [];
    counties: County[] = [];
    schoolDistricts: SchoolDistrict[] = [];
    allSchoolDistricts: SchoolDistrict[] = [];

    userRegion: Region = new Region();
    userRegionLoaded: boolean = false;
    userRegionSchoolDistricts: SchoolDistrict[] = [];

    loaded$: Observable<boolean>;
    isLoading: boolean = false;
    changed: boolean = true;

    subs: Subscription[] = [];

    pageIndex: number = 0;
    pageSize: number = 5;

    constructor(private fb: UntypedFormBuilder, private schoolDistrictService: DistrictEntityService, private helperService: HelperService) {
        this.schoolDistrictForm = this.fb.group({
            schoolDistrictSelect: [''],
            filter: ['']
        });

        this.loaded$ = schoolDistrictService.loaded$;
        let loadedSub = this.loaded$?.subscribe((loaded) => {
            if (!loaded || this.disabled) {
                this.schoolDistrictSelect.disable();
            } else {
                this.schoolDistrictSelect.enable();
            }
        });
        this.subs.push(loadedSub);
    }

    subscribers() {
        let schoolDistrictEntitiesSub = this.schoolDistrictService.entities$.subscribe((schoolDistricts: SchoolDistrict[]) => {
            this.allSchoolDistricts = schoolDistricts;
            this.schoolDistrictIds = this.allSchoolDistricts.length > 0 ? this.allSchoolDistricts.map((schoolDistrict) => schoolDistrict.ID!) : [];
            this.changed = true;

            this.loadData(true);
        });
        this.subs.push(schoolDistrictEntitiesSub);

        if (this.autoRefreshOnRegionChange) {
            let selectedRegionsSub = this.helperService.selectedRegions$.subscribe((selectedRegions: Region[]) => {
                if (selectedRegions?.length <= 0) {
                    let lstUserProfileSub2 = this.helperService.lstUserProfileRegion$.subscribe((userRegion: Region[]) => {
                        this.regions = userRegion;

                        this.autoRefreshOnRegionChange ? this.loadData(true) : null;
                    });

                    this.subs.push(lstUserProfileSub2);
                } else {
                    this.regions = selectedRegions;

                    this.autoRefreshOnRegionChange ? this.loadData(true) : null;
                }

                this.changed = true;
            });
            this.subs.push(selectedRegionsSub);
        } else {
            let lstUserProfileSub = this.helperService.lstUserProfileRegion$.subscribe((userRegion: Region[]) => {
                this.userRegion = userRegion[0];
            });
            this.subs.push(lstUserProfileSub);
        }
        let selectedCountiesSub = this.helperService.selectedCounties$.subscribe((counties) => {
            this.counties = counties;
            this.changed = true;
            this.loadData(true);
        });
        this.subs.push(selectedCountiesSub);

        let filterInputSub = this.filterInput!.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value: string) => {
            if (value.length === 0) {
                this.filteredSchoolDistricts = this.schoolDistricts;
            } else {
                this.filteredSchoolDistricts = this.schoolDistricts.filter((schoolDistrict) =>
                    schoolDistrict.DISTRICTS.toLowerCase().includes(value.toLowerCase())
                );
                this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
            }
        });
        this.subs.push(filterInputSub);
    }

    emitSelect(schoolDistrictIds: number[]) {
        let output: SchoolDistrict[] = [];
        //console.log('district dropdown now running emitSelect on');

        this.selectedSchoolDistrictIds = Array.from(new Set(this.selectedSchoolDistrictIds.concat(schoolDistrictIds)));

        if (schoolDistrictIds.length > 0) {
            this.schoolDistricts.forEach((filteredSchoolDistrict) => {
                schoolDistrictIds.forEach((schoolDistrictId) => {
                    if (schoolDistrictId == filteredSchoolDistrict.ID) {
                        output.push(filteredSchoolDistrict);
                    }
                });
            });
        }
        //console.log(this.selectedSchoolDistrictIds);

        // View updates
        this.selectedSchoolDistrictName = output[0]?.DISTRICTS;
        this.helperService.setSelectedDistrictObject(output);
        this.filterInput?.patchValue('');
        output[0] ? this.goToPage(output[0].ID!) : this.goToPage(0);
        //Emits the code to the subscriber.
        this.eventEmitterSchoolDistrict.emit(output);
    }

    ngOnInit(): void {
        if (this.customLabel) {
            this.fieldLabel = this.customLabel;
        } else {
            this.fieldLabel = 'School District';
        }

        this.subscribers();

        // let initLoadedSub = this.loaded$?.subscribe((loaded) => {
        //     if (loaded) {
        //         //console.log('district dropdown loaded with');
        //         setTimeout(() => {
        //             if (this.selectSchoolDistrictIds.length > 0) {
        //                 this.select(this.selectSchoolDistrictIds);
        //                 //console.log(this.selectSchoolDistrictIds);
        //                 this.emitSelect(this.selectSchoolDistrictIds);
        //             } else {
        //                 this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
        //             }

        //             //        this.selectSchoolDistrictIds = [];
        //             initLoadedSub.unsubscribe();
        //         }, 500);
        //     }
        // });
        // this.subs.push(initLoadedSub);
    }

    ngOnDestroy() {
        this.subs.forEach((sub) => {
            sub.unsubscribe();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.autoRefreshOnRegionChange) {
            this.subscribers();
        }

        if (this.reset) {
            this.deSelectAll();
        }

        if (this.disabled) {
            this.schoolDistrictSelect.disable();
        } else {
            this.schoolDistrictSelect.enable();
        }

        if (changes['selectSchoolDistrictIds']) {
            if (this.selectSchoolDistrictIds.length > 0) {
                this.select(this.selectSchoolDistrictIds);

                this.emitSelect(this.selectSchoolDistrictIds);
            } else {
                this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
            }

            this.selectSchoolDistrictIds = [];
        }
    }

    loadData(open: any) {
        if (open) {
            this.filterRef ? this.filterRef.nativeElement.focus() : null;
            if (!this.changed || this.isLoading) return;

            this.schoolDistricts = [];
            this.isLoading = true;
            this.schoolDistrictSelect.disable();
            this.changed = false;
            this.filterInput!.patchValue('');

            let workingRegions = this.removeDuplicateIDs(this.regions);
            let workingSchoolDistricts = this.allSchoolDistricts;
            this.counties = this.removeDuplicateIDs(this.counties);
            let params: any = {};
            params['regions'] = workingRegions.map((region) => region.ID);
            params['counties'] = this.counties.map((county) => county.ID);

            if (this.regions.length > 0) {
                workingSchoolDistricts = workingSchoolDistricts.filter((schoolDistrict) =>
                    this.regions.find((region) => region.ID === schoolDistrict.REGION.ID)
                );
            }
            //console.log('district dropdown filtering by county');
            if (this.counties.length > 0) {
                workingSchoolDistricts = workingSchoolDistricts.filter((schoolDistrict) =>
                    this.counties.find((county) => county.ID === schoolDistrict.COUNTy.ID)
                );
            }

            //console.log('workingSchoolDistricts:');
            //console.log(workingSchoolDistricts);
            this.schoolDistricts = Array.from(new Set(workingSchoolDistricts));

            this.schoolDistricts.sort((a, b) => a.DISTRICTS.localeCompare(b.DISTRICTS));
            this.filteredSchoolDistricts = this.schoolDistricts;

            if (this.schoolDistricts.length === 1 && !this.disableAutoComplete) {
                this.schoolDistrictSelect.patchValue([]);
                this.selectAll();
                this.emitSelect(this.selectedSchoolDistrictIds);
            } else {
                this.select(this.selectedSchoolDistrictIds);
                this.emitSelect(this.selectedSchoolDistrictIds);
            }
            if (!this.disabled) {
                this.schoolDistrictSelect.enable();
            }
            //console.log('selectedSchoolDistrictIds at this pointA is:');
            //console.log(this.selectedSchoolDistrictIds);
            this.changed = false;
            this.isLoading = false;
        } else {
            //console.log('selectedSchoolDistrictIds at this pointB is:');
            //console.log(this.selectedSchoolDistrictIds);

            this.select(this.selectedSchoolDistrictIds);
            this.emitSelect(this.selectedSchoolDistrictIds);
        }
    }

    removeDuplicateIDs(arr: Array<any>): Array<any> {
        arr = arr.filter((value, index, self) => index === self.findIndex((t) => t.place === value.place && t.ID === value.ID));

        return arr;
    }

    select(selection: any) {
        // Get current page
        this.filteredSchoolDistricts = this.schoolDistricts;

        if (this.isMultiple) {
            // Remove all selections in current page
            let currentPageIds = this.filteredSchoolDistrictsShow.map((show) => show.ID);
            this.selectedSchoolDistrictIds = this.selectedSchoolDistrictIds.filter(
                (selectedSchoolDistrictId) => !currentPageIds.includes(selectedSchoolDistrictId)
            );

            // Add current selection to total array
            this.selectedSchoolDistrictIds = Array.from(new Set(this.selectedSchoolDistrictIds.concat(selection)));

            // Remove all IDs that are not in available school districts
            let filteredSchoolDistricts = this.filteredSchoolDistricts.map((filteredSchoolDistrict) => filteredSchoolDistrict.ID);
            this.selectedSchoolDistrictIds = this.selectedSchoolDistrictIds.filter((selectedSchoolDistrictId) =>
                filteredSchoolDistricts.includes(selectedSchoolDistrictId)
            );

            this.schoolDistrictSelect.patchValue(this.selectedSchoolDistrictIds);
        } else {
            if (Array.isArray(selection)) {
                this.schoolDistrictSelect.patchValue(selection[0]);
                this.selectedSchoolDistrictIds = selection;
            } else {
                this.schoolDistrictSelect.patchValue(selection);
                this.selectedSchoolDistrictIds = [selection];
            }

            this.goToPage(this.schoolDistrictSelect.value);
            this.emitSelect(this.selectedSchoolDistrictIds);
        }
    }

    selectAll() {
        let selected = this.schoolDistrictSelect.value; // Currently selected

        if (this.isMultiple) {
            selected = Array.isArray(selected) ? selected : [];
            this.selectedSchoolDistrictIds = Array.from(new Set(selected.concat(this.filteredSchoolDistricts.map((schoolDistrict) => schoolDistrict.ID!)))); // Select all filtered (if any filter)
            this.schoolDistrictSelect.patchValue(this.selectedSchoolDistrictIds);
        } else {
            this.selectedSchoolDistrictIds = this.filteredSchoolDistricts.map((schoolDistrict) => schoolDistrict.ID!);
            this.schoolDistrictSelect.patchValue(this.selectedSchoolDistrictIds[0]);
        }
    }

    deSelectAll() {
        this.reset = false;
        this.schoolDistrictSelect.patchValue([]);
        this.selectedSchoolDistrictIds = [];
        this.filterInput?.patchValue('');
        this.emitSelect([]);
    }

    close() {
        this.matSelect.close();
    }

    get schoolDistrictSelect(): any {
        return this.schoolDistrictForm.get('schoolDistrictSelect');
    }
    get filterInput(): AbstractControl | null {
        return this.schoolDistrictForm.get('filter');
    }

    onPageChange($event: any) {
        $event.pageIndex = $event.pageIndex < 0 ? 0 : $event.pageIndex;

        this.pageIndex = $event.pageIndex;
        this.pageSize = $event.pageSize;

        this.filteredSchoolDistrictsShow = this.filteredSchoolDistricts.slice(
            $event.pageIndex * $event.pageSize,
            $event.pageIndex * $event.pageSize + $event.pageSize
        );
    }

    goToPage(id: number) {
        let index = this.filteredSchoolDistricts.findIndex((filteredSchoolDistrict) => filteredSchoolDistrict.ID === id);
        let pageIndex = Math.floor(index / this.pageSize);

        if (this.paginator) {
            this.paginator.pageIndex = pageIndex;
        }
        this.onPageChange({ pageIndex: pageIndex, pageSize: this.pageSize });
    }
}
